import { useEffect, useState, useRef } from "react";
import { Upload, ExternalLink, FileText, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/lib/content";

export default function AdminLicense() {
  const { content, refetch } = useContent();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setUrl(content?.license?.file_url || ""); }, [content]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUrl(data.url);
      toast.success("Файл загружен — не забудьте «Сохранить»");
    } catch { toast.error("Ошибка загрузки"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/content/license", { key: "license", data: { file_url: url } });
      toast.success("Сохранено");
      refetch();
    } catch { toast.error("Ошибка сохранения"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl w-full">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-wider text-accent mb-2">Файлы</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Лицензия и файлы</h1>
        <p className="text-sm text-muted-foreground mt-2">Загрузите PDF-файл лицензии — он откроется в новой вкладке при клике на кнопку «Лицензия» в шапке.</p>
      </div>

      <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
        <div className="flex items-center gap-4">
          <div className="size-16 rounded-xl bg-foreground text-background flex items-center justify-center shrink-0">
            <FileText className="size-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Текущий файл</div>
            {url ? (
              <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-mono truncate hover:underline" data-testid="license-current-url">
                {url} <ExternalLink className="size-3" />
              </a>
            ) : <div className="text-sm text-muted-foreground">Not set</div>}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <input ref={ref} type="file" hidden onChange={upload} accept="application/pdf,image/*" />
          <button
            onClick={() => ref.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-border text-sm font-medium hover:bg-muted disabled:opacity-50"
            data-testid="license-upload-btn"
          >
            <Upload className="size-4" /> Загрузить новый файл
          </button>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="или вставьте URL..."
            className="flex-1 h-10 px-3 rounded-lg border border-input bg-background text-sm min-w-[200px]"
            data-testid="license-url-input"
          />
          <button
            onClick={save}
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-foreground text-background text-sm font-semibold disabled:opacity-50"
            data-testid="license-save-btn"
          >
            <Save className="size-4" /> Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}
